import { kMedSummaryApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'
import { ApplyUpdateResponse, ApplyUpdateResponseSchema } from '../store/schemas'

/**
 * Re-enqueues a FAILED_FILES update run for its failed files only.
 */
export async function postRetryFailed(assetId: string, updateId: string): Promise<ApplyUpdateResponse> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMedSummaryApiBasePath}/asset/${assetId}/updates/${updateId}/retry-failed`
    const response = await fetch(apiUrl, requestOptions)

    if (!response.ok) {
      const data = await response.json()
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    const data = await response.json()
    return ApplyUpdateResponseSchema.parse(data)
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
